import { useQuery, useMutation, skipToken } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { useEffect, useEffectEvent } from 'react';
import { ErrorLike } from '@apollo/client';

import { PageDrawer } from 'common/components/PageDrawer';
import {
    OffersSettingsDocument,
    UpdateOffersSettingsDocument,
    IFileFragment,
} from 'graphql/types';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { getImageId } from 'common/utils/image';
import { IPageDrawerProps } from 'common/components/PageDrawer/PageDrawer';
import { useApolloError } from 'common/hooks/useApolloError';
import {
    TOfferOverviewFormValues,
    OfferOverviewForm,
} from 'offer/forms/OfferOverviewForm';
import { useAppPermissions } from 'user/hooks/usePermissions';
import { PageTitle } from 'common/components/PageTitle';

type TFormValues = TOfferOverviewFormValues & { image?: IFileFragment };

interface IProps extends IPageDrawerProps {
    open?: boolean;
    onClose?: () => void;
}

export const OfferSettingsDrawer = ({ open, onClose, ...other }: IProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();

    const { canUpdate: canUpdateOfferSettings = false } = useAppPermissions(
        'offer',
        'management'
    );

    const { showApolloError } = useApolloError();

    const {
        data: offersSettingsData,
        loading: offersSettingsLoading,
        error: offersSettingsError,
    } = useQuery(
        OffersSettingsDocument,
        canUpdateOfferSettings ? {} : skipToken
    );

    const onError = useEffectEvent((error: ErrorLike) => {
        showApolloError(error);
    });

    useEffect(() => {
        if (offersSettingsError) {
            onError(offersSettingsError);
        }
    }, [offersSettingsError]);

    const [updateOffersSettings, { loading: offersSettingsUpdateLoading }] =
        useMutation(UpdateOffersSettingsDocument);

    if (!canUpdateOfferSettings) return null;

    const { offersSettings } = offersSettingsData || {};

    const initialValues = {
        title: offersSettings?.title || '',
        titleColor: offersSettings?.titleColor?.replace('#', '') || '',
        description: offersSettings?.description || '',
        image: offersSettings?.image,
        publishTraining: !!offersSettings?.publishTraining,
        publishElearning: !!offersSettings?.publishElearning,
        publishMeetingWebinar: !!offersSettings?.publishMeetingWebinar,
    };

    const handleSubmit = async (values: TFormValues) => {
        const { titleColor, image, ...otherValues } = values;
        const { image: currentImage } = offersSettings || {};

        const newValues = {
            titleColor: titleColor
                ? titleColor.includes('#')
                    ? titleColor
                    : `#${titleColor}`
                : undefined,
            imageId: getImageId(currentImage, image),
            ...otherValues,
        };

        try {
            await updateOffersSettings({
                variables: {
                    settings: newValues,
                },
                update: (cache, result) => {
                    const { offersSettings } =
                        result.data?.updateOffersSettings || {};

                    if (!offersSettings) return;

                    cache.writeQuery({
                        query: OffersSettingsDocument,
                        data: { offersSettings },
                    });
                },
                refetchQueries: [{ query: OffersSettingsDocument }],
                awaitRefetchQueries: true,
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(translate('settingsUpdated'), {
            variant: 'success',
        });

        onClose?.();
    };

    const loading = offersSettingsLoading || offersSettingsUpdateLoading;

    return (
        <PageDrawer open={open} onClose={onClose} {...other}>
            <PageTitle>{translate('offerSettingsDrawer.pageTitle')}</PageTitle>

            <OfferOverviewForm
                disabled={loading}
                initialValues={initialValues}
                loading={loading}
                onClose={onClose}
                onSubmit={handleSubmit}
            />
        </PageDrawer>
    );
};
